import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { GetPendingInvitations } from './usePendingInvitations'

const ResendInvitation = graphql(/* GraphQL */ `
  mutation ResendInvitation($input: ResendInvitationInput!) {
    resendInvitation(input: $input) {
      ... on ResendInvitationSuccess {
        invitation {
          id
          invitedAt
          expiresAt
        }
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface UseResendInvitationReturn {
  resendInvitation: (input: {
    organizationId: string
    invitationId: string
  }) => Promise<{ success: boolean; error?: string }>
  loading: boolean
}

/**
 * `organizationId` is no longer part of the GraphQL input (the backend
 * resolves the org from the invitation), but the hook still takes it so
 * it can refetch the org-scoped `pendingInvitations` query after a
 * successful resend.
 */
export const useResendInvitation = (): UseResendInvitationReturn => {
  const [mutate, { loading }] = useMutation(ResendInvitation)

  const resendInvitation = async (input: {
    organizationId: string
    invitationId: string
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data } = await mutate({
        variables: { input: { invitationId: input.invitationId } },
        refetchQueries: [
          {
            query: GetPendingInvitations,
            variables: { input: { organizationId: input.organizationId } },
          },
        ],
      })

      const result = data?.resendInvitation
      if (!result) {
        return { success: false, error: 'Failed to resend invitation' }
      }
      if (result.__typename === 'ResendInvitationSuccess') {
        return { success: true }
      }
      if (result.__typename === 'NotFoundError') {
        return { success: false, error: result.message }
      }
      return { success: false, error: 'Unknown error occurred' }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      }
    }
  }

  return { resendInvitation, loading }
}
